import {NOOP_FUNCTION} from "../../stem-core/base/Utils";
import {RemoveHandle} from "../../stem-core/base/Dispatcher";
import {onCurrentAndFuturePageAccess} from "./PageAccessHelpers.js";

// A held scroll lock. A RemoveHandle so holders can hand it straight to addCleanupJob. scrollbarWidth is
// measured when the lock is taken, for callers offsetting fixed elements so they don't jump.
export interface ScrollLockHandle extends RemoveHandle {
    scrollbarWidth: number;
}

export function removeScrollOnScrim(): ScrollLockHandle {
    const element = document.documentElement.clientHeight !== document.documentElement.scrollHeight ? document.documentElement : document.body;
    const scrollbarWidth = window.innerWidth - element.clientWidth;
    const computedStyle = getComputedStyle(element);

    if (computedStyle.getPropertyValue("overflow").endsWith("hidden")) {
        // The page already blocks scroll; don't touch it.
        return {
            scrollbarWidth,
            remove: NOOP_FUNCTION,
        };
    }

    // Snapshot the inline styles, not the computed ones: writing a computed value back would pin e.g.
    // "overflow: visible" inline and fight the page's own CSS.
    const inlineOverflow = element.style.overflow;
    const inlinePaddingRight = element.style.paddingRight;
    const initialRightPadding = computedStyle.getPropertyValue("padding-right");

    Object.assign(element.style, {
        overflow: "hidden",
        paddingRight: `${parseFloat(initialRightPadding) + scrollbarWidth}px`,
    });

    return {
        scrollbarWidth,
        remove: () => {
            element.style.overflow = inlineOverflow;
            element.style.paddingRight = inlinePaddingRight;
        },
    };
}

// One refcounted lock shared by every scrim source (the wallet/popup flow and the gated-content gates), so
// nested locks can't write overflow:hidden twice and a surviving one can't freeze the page across an SPA
// navigation.
class BodyScrollLock {
    private underlyingLock: ScrollLockHandle | null = null;
    // Each live holder, mapped to the liveness check its owner registered. Holders without one (the wallet)
    // are never swept.
    private holders = new Map<ScrollLockHandle, (() => boolean) | undefined>();
    private routeSweepInstalled = false;

    acquire(isOwnerAlive?: () => boolean): ScrollLockHandle {
        this.installRouteSweep();
        if (!this.underlyingLock) {
            this.underlyingLock = removeScrollOnScrim();
        }
        const holder: ScrollLockHandle = {
            scrollbarWidth: this.underlyingLock.scrollbarWidth,
            remove: () => this.releaseHolder(holder),
        };
        this.holders.set(holder, isOwnerAlive);
        return holder;
    }

    private releaseHolder(holder: ScrollLockHandle) {
        if (!this.holders.delete(holder)) {
            return; // Idempotent, so a double release can't drop the count below the live holders.
        }
        if (this.holders.size === 0) {
            this.underlyingLock?.remove();
            this.underlyingLock = null;
        }
    }

    // Installed on first use, so a merchant that never locks scroll doesn't pay for the page-access watcher.
    private installRouteSweep() {
        if (this.routeSweepInstalled) {
            return;
        }
        this.routeSweepInstalled = true;
        onCurrentAndFuturePageAccess(() => {
            for (const [holder, isOwnerAlive] of [...this.holders]) {
                if (isOwnerAlive && !isOwnerAlive()) {
                    this.releaseHolder(holder);
                }
            }
        });
    }
}

export const bodyScrollLock = new BodyScrollLock();